/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { Card, useMediaQuery } from '@veneer/core';
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import FallbackImage from '../../assets/images/device-details-page-fallback.png';
import HPLogo from '../../assets/images/hp-plus-logo.png';
import { breakpoints } from '../../base';
import { debounce, generateID } from '../../base/helper';
import StatusList from '../status-list';
import './styles.scss';
import { PrimaryDeviceCardProps } from './types';

/**
 * UI Toolkit : Primary device card
 * @public
 */
const TPrimaryDeviceCard: React.FC<PrimaryDeviceCardProps> = ({
  id = generateID('clientos-ui-toolkit-primary-device-card'),
  deviceNickName,
  deviceModelName,
  deviceImage,
  hPPlusEnrolled,
  printerSupplyWidget,
  children,
  onCardClick,
  actionList,
  loading
}) => {
  const [isDeviceImageError, setIsDeviceImageError] = useState(false);
  const [inlineDeviceNames, setInlineDeviceNames] = useState(true);
  const imageContainer = useRef(null);
  const cardContainer = useRef(null);
  const printerSupplyWidgetRef = useRef(null);
  const childrenRef = useRef(null);
  const actionListRef = useRef(null);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.md})`);

  const DefineInlineDeviceNames = useCallback(() => {
    const contentOriginalWidth =
      (printerSupplyWidgetRef?.current?.clientWidth ?? 0) +
      (childrenRef?.current?.clientWidth ?? 0) +
      (actionListRef?.current?.clientWidth ?? 0) +
      (imageContainer?.current?.clientWidth ?? 0);

    if (contentOriginalWidth > cardContainer?.current?.clientWidth) {
      setInlineDeviceNames(false);
    } else {
      setInlineDeviceNames(true);
    }
  }, []);

  useLayoutEffect(() => {
    DefineInlineDeviceNames();
  }, [DefineInlineDeviceNames]);

  useLayoutEffect(() => {
    const debouncedAddNameMargin = debounce(DefineInlineDeviceNames, 300);
    window.addEventListener('resize', debouncedAddNameMargin);

    return () => {
      window.removeEventListener('resize', debouncedAddNameMargin);
    };
  }, [DefineInlineDeviceNames]);

  return (
    <Card
      appearance="outlined"
      className={`clientos-ui-toolkit-primary-device-card card-wrapper${loading ? ' clientos-ui-toolkit-primary-device-card--loading' : ''}`}
      id={id}
      onClick={onCardClick}
      content={
        <div
          className="clientos-ui-toolkit-primary-device-card__card-content-container"
          ref={cardContainer}
          id={`${id}__card-content-container`}
        >
          {!inlineDeviceNames && (
            <div
              className={`clientos-ui-toolkit-primary-device-card__card-content-container__top-lined-device-name ${hPPlusEnrolled ? 'clientos-ui-toolkit-primary-device-card__card-content-container__top-lined-device-name--hp-plus' : ''}`}
              id={`${id}__top-lined-device-name`}
            >
              <h2
                className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__model__device-name ${isDesktop ? 'title-small--medium' : 'title-medium'}`}
                id={`${id}__device-nickname`}
              >
                {deviceNickName}
              </h2>
              <h3
                className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__model__model-name ${isDesktop ? 'body-large' : 'subtitle-large'}`}
                id={`${id}__device-model-name`}
              >
                {deviceModelName}
              </h3>
            </div>
          )}
          <div
            className="clientos-ui-toolkit-primary-device-card__card-content-container__inner"
            id={`${id}__card-content-container__inner`}
          >
            <div
              className="clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container"
              id={`${id}__info-container`}
            >
              {inlineDeviceNames && (
                <div
                  className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__model`}
                  id={`${id}__info-container__model`}
                >
                  <h2
                    className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__model__device-name ${isDesktop ? 'title-small--medium' : 'title-medium'}`}
                    id={`${id}__device-nickname`}
                  >
                    {deviceNickName}
                  </h2>
                  <h3
                    className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__model__model-name ${isDesktop ? 'body-large' : 'subtitle-large'}`}
                    id={`${id}__device-model-name`}
                  >
                    {deviceModelName}
                  </h3>
                </div>
              )}
              <div
                className="clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__card-meta"
                id={`${id}__info-container__card-meta`}
              >
                {printerSupplyWidget && (
                  <div
                    ref={printerSupplyWidgetRef}
                    className="clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__card-meta__placeholder"
                    id={`${id}__printer-supply-widget`}
                  >
                    {printerSupplyWidget}
                  </div>
                )}
                {children && (
                  <div
                    ref={childrenRef}
                    className="clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__card-meta__status"
                    id={`${id}__children`}
                  >
                    {children}
                  </div>
                )}
                {actionList && (
                  <div
                    className="clientos-ui-toolkit-primary-device-card__card-content-container__inner__info-container__card-meta__status-list"
                    ref={actionListRef}
                    id={`${id}__action-list`}
                  >
                    <StatusList statusItemList={actionList} />
                  </div>
                )}
              </div>
            </div>
            <div
              className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__images${hPPlusEnrolled ? '--hp-plus' : ''}${isDeviceImageError ? '--fallback' : ''}`}
              id={`${id}__images`}
            >
              <div
                ref={imageContainer}
                className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__images__image-container`}
                id={`${id}__image-container`}
              >
                <img
                  className={`clientos-ui-toolkit-primary-device-card__card-content-container__inner__images__image-container__device-image ${isDeviceImageError ? `clientos-ui-toolkit-primary-device-card__card-content-container__inner__images__image-container__device-image--fallback` : ''}`}
                  src={deviceImage}
                  id={`${id}__device-image`}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.src = FallbackImage;
                    setIsDeviceImageError(true);
                  }}
                />
              </div>
            </div>
          </div>
          {hPPlusEnrolled && (
            <div
              className="clientos-ui-toolkit-primary-device-card__card-content-container__hp-plus-container"
              id={`${id}__hp-plus-container`}
            >
              <img
                id={`${id}__hp-plus-logo`}
                className="clientos-ui-toolkit-primary-device-card__card-content-container__hp-plus-container__image"
                src={HPLogo}
                alt="hp_logo"
              />
            </div>
          )}
        </div>
      }
    />
  );
};
export const PrimaryDeviceCardAnalytics = withAnalytics(TPrimaryDeviceCard);
export default memo(TPrimaryDeviceCard);
